import { useCallback, useEffect, useRef, useState } from "react";

const getSpeechRecognition = () => {
  if (typeof window === "undefined") return null;
  return window.SpeechRecognition || window.webkitSpeechRecognition || null;
};

/**
 * Browser speech-to-text. Toggle listening like a video-call mute button:
 * off by default; unmute to start appending transcript; mute again to stop.
 */
export default function useSpeechToText({ onTranscript, language = "en-IN" } = {}) {
  const [isListening, setIsListening] = useState(false);
  const [isSupported] = useState(() => Boolean(getSpeechRecognition()));
  const recognitionRef = useRef(null);
  const isListeningRef = useRef(false);
  const onTranscriptRef = useRef(onTranscript);

  useEffect(() => {
    onTranscriptRef.current = onTranscript;
  }, [onTranscript]);

  useEffect(() => {
    const SpeechRecognitionCtor = getSpeechRecognition();
    if (!SpeechRecognitionCtor) return undefined;

    const recognition = new SpeechRecognitionCtor();
    recognition.continuous = true;
    recognition.interimResults = false;
    recognition.lang = language;

    recognition.onresult = (event) => {
      let chunk = "";
      for (let i = event.resultIndex; i < event.results.length; i += 1) {
        if (event.results[i].isFinal) {
          chunk += event.results[i][0].transcript;
        }
      }
      const trimmed = chunk.trim();
      if (trimmed && onTranscriptRef.current) {
        onTranscriptRef.current(trimmed);
      }
    };

    recognition.onerror = () => {
      isListeningRef.current = false;
      setIsListening(false);
    };

    recognition.onend = () => {
      if (isListeningRef.current) {
        try {
          recognition.start();
        } catch {
          isListeningRef.current = false;
          setIsListening(false);
        }
      }
    };

    recognitionRef.current = recognition;

    return () => {
      isListeningRef.current = false;
      try {
        recognition.stop();
      } catch {
        /* ignore */
      }
      recognitionRef.current = null;
    };
  }, [language]);

  const stop = useCallback(() => {
    isListeningRef.current = false;
    setIsListening(false);
    try {
      recognitionRef.current?.stop();
    } catch {
      /* ignore */
    }
  }, []);

  const start = useCallback(() => {
    if (!recognitionRef.current) return false;
    try {
      isListeningRef.current = true;
      setIsListening(true);
      recognitionRef.current.start();
      return true;
    } catch {
      isListeningRef.current = false;
      setIsListening(false);
      return false;
    }
  }, []);

  const toggle = useCallback(() => {
    if (isListeningRef.current) {
      stop();
    } else {
      return start();
    }
    return true;
  }, [start, stop]);

  return {
    isListening,
    isSupported,
    toggle,
    start,
    stop,
  };
}
